import React, { useCallback, useEffect, useState } from 'react';
import { TMarkerType } from '@awtg-ltd/react-map-component/dist/constants/common';
import cn from 'classnames';

import { Button, Confirm, Modal, SpinnerOverlay } from '@components/common';
import KpiSelectModalOnMap from '@components/Kpi/KpiSelectModalOnMap';
import AssetInfo from '@components/Map/MarkerShortInfo/AssetInfo';
import SiteInfo from '@components/Map/MarkerShortInfo/SiteInfo';

import { useMutateDeleteAsset } from '@hooks/assets/useMutateDeleteAsset';
import { useQueryCellsForSite } from '@hooks/crowdCells/useQueryCellsForSite';
import { useQueryAllSectors } from '@hooks/sectors/useQuerySectors';
import { useQueryUser } from '@hooks/useQueryUser';

import { auth, kpi, map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	addSiteIdToRenderSector,
	removeSelectedActiveAssetMarker,
	removeSiteIdFromRenderSector,
	setAssetLocationEditMode,
	setMarkerForShortInfo,
	setShowMarkerFullInfo,
	setShowMarkerShortInfo,
	setSiteLocationEditMode,
} from '@slices/map';

import { TAsset } from '@interfaces/assets/asset';
import { ICellForSectorDto } from '@interfaces/map/dto/sectorsDTO';
import { ISiteMarker } from '@interfaces/map/map';

import { EAssetRentStatuses } from '@enums/asset';
import { EAggregationTypes } from '@enums/kpi-api';
import { EDataType, EMarkerTypes } from '@enums/map';

import styles from './MarkerShortInfo.module.scss';

interface IMarkerBasicInfoProps {
	markerShort: Record<string, unknown>;
	assetFull?: TAsset;
	siteFull?: ISiteMarker;
	position: {
		x: number;
		y: number;
	};
	offset?: number;
	markerType: TMarkerType | null;
	cellsData?: ICellForSectorDto[];
}

const PARENT_TYPE_LETTER_WIDTH = 12;
const SUBTYPE_LETTER_WIDTH = 6;

const ASSET_LOCATION_EDIT_DISABLED_TOOLTIP = 'This asset has been rented and cannot be moved';

const MarkerShortInfo = ({
	assetFull,
	markerShort,
	markerType,
	offset = 15,
	position,
	siteFull,
}: IMarkerBasicInfoProps) => {
	const dispatch = useAppDispatch();
	const { accessPermission } = useAppSelector(auth);
	const {
		activeCharts,
		aggregationTypeOnMap,
		siteIdsOfActiveCharts,
	} = useAppSelector(kpi);
	const {
		siteIdsWithinActivePolygons,
		selectedSiteIdsByShiftKey,
		sectors: { selectedSiteIds, isSectorsShown: isSectorsShownGlobally },
	} = useAppSelector(map);

	const { user } = useQueryUser();

	const {
		cellsForSite,
		isLoading: isCellsLoading,
	} = useQueryCellsForSite({
		siteId: siteFull?.id,
	});

	useEffect(() => {
		dispatch(setShowMarkerFullInfo(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const {
		assetOwnerOrganizationAdministrator,
		assetOwnerAttributeUpdater,
		networkOperatorOrganizationAdministrator,
		networkOperatorAssetEnquirer,
		networkOperatorAssetViewer,
		platformOwnerAdministrator,
		networkOperatorTelecomSitesAttributeUpdater,
		networkOperatorTelecomSitesViewer,
		assetConsumerOrganizationAdministrator,
		assetConsumerAssetEnquirer,
		assetConsumerAssetViewer,
	} = accessPermission;

	const { allSectors } = useQueryAllSectors();

	const [typeContainerMinWidth, setTypeContainerMinWidth] = useState('unset');
	const [elementPosition, setElementPosition] = useState<{ top: string; left: string }>();

	const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);
	const [isKpiAnalysisModalOpen, setIsKpiAnalysisModalOpen] = useState(false);

	const toggleKpiAnalysisModal = () => setIsKpiAnalysisModalOpen(prevState => !prevState);

	const closeDeleteConfirm = () => {
		dispatch(setMarkerForShortInfo(null));
		setIsDeleteConfirm(false);
	};

	const removeAssetFromActiveList = () => {
		dispatch(removeSelectedActiveAssetMarker(assetFull?.id as any));
	};

	const { handleDeleteAsset, isDeletingAsset } = useMutateDeleteAsset([
		closeDeleteConfirm,
		removeAssetFromActiveList,
	]);

	const { organisationId } = useQueryUser();

	const kpiChartsForSite = activeCharts.filter((chart) => {
		return !!chart.sites.find((site) => site.siteId === siteFull?.siteId);
	});

	const isAssetsMode = markerType === EDataType.assets;
	const isSitesMode = markerType === EDataType.sites;
	const isAssetOwner = organisationId === assetFull?.owner?.id;
	const isCluster = markerShort.markerType !== EMarkerTypes.point;
	const isAssetChangesAvailable = (assetOwnerOrganizationAdministrator || assetOwnerAttributeUpdater)
		&& isAssetOwner
		&& isAssetsMode;
	const isSiteChangesAvailable = (
		networkOperatorOrganizationAdministrator ||
		networkOperatorTelecomSitesAttributeUpdater ||
		networkOperatorTelecomSitesViewer ||
		assetConsumerOrganizationAdministrator
	) && isSitesMode && siteFull?.assetId === null && siteFull?.ownerId === user?.organization?.id;
	const isAssetOwnerAvailable = (
		platformOwnerAdministrator ||
		networkOperatorOrganizationAdministrator ||
		networkOperatorAssetEnquirer ||
		networkOperatorAssetViewer ||
		assetConsumerOrganizationAdministrator ||
		assetConsumerAssetEnquirer ||
		assetConsumerAssetViewer
	);

	const isMarkerInsideActivePolygon = isSitesMode && !!siteIdsWithinActivePolygons.find((id) => id === siteFull?.id);
	const isSelectedByShift = isSitesMode && !!selectedSiteIdsByShiftKey.find((id) => id === siteFull?.id);
	const isSelectedForKpiChart = isSitesMode && !!siteIdsOfActiveCharts.find((id) => id === siteFull?.id);
	const isSiteLevelAggregationKpis = (
		aggregationTypeOnMap === EAggregationTypes.siteLevel ||
		aggregationTypeOnMap === EAggregationTypes.siteLevelAggregatedCells
	);

	const isKPIAnalysisButtonVisible = (
		isSitesMode &&
		isSiteLevelAggregationKpis &&
		!!activeCharts.length &&
		(isMarkerInsideActivePolygon || isSelectedForKpiChart || isSelectedByShift)
	);

	const isKPIAnalysisButtonEnabled = isKPIAnalysisButtonVisible && !!kpiChartsForSite.length;

	const enableAssetLocationEditMode = () => {
		if (assetFull) dispatch(setAssetLocationEditMode(assetFull));
	};

	const enableSiteLocationEditMode = () => {
		if (siteFull) {
			dispatch(setSiteLocationEditMode(siteFull));
		}
	};

	const toggleDeleteConfirm = () => setIsDeleteConfirm(!isDeleteConfirm);

	const onDeleteAsset = useCallback(() => {
		return handleDeleteAsset(assetFull?.id as any);
	}, [assetFull?.id, handleDeleteAsset]);

	useEffect(() => {
		if (position) {
			setElementPosition({
				top: `${position.y + offset}px`,
				left: `${position.x + offset}px`,
			});
		}
	}, [offset, position]);

	useEffect(() => {
		if (assetFull?.assetType || assetFull?.assetSubType) {
			const typeContainerWidth = assetFull?.assetType?.assetTypeName?.length * PARENT_TYPE_LETTER_WIDTH;
			const subTypeContainerWidth = assetFull?.assetSubType?.assetSubTypeName?.length * SUBTYPE_LETTER_WIDTH;
			const minWidth = Math.max(typeContainerWidth, subTypeContainerWidth);

			setTypeContainerMinWidth(`${minWidth}px`);
		}
	}, [assetFull]);

	const [isSectorsRendered, setIsSectorsRendered] = useState(false);

	useEffect(() => {
		setIsSectorsRendered(selectedSiteIds.includes(siteFull?.id as string));
	}, [selectedSiteIds, siteFull?.id]);

	const handleToggleSectors = () => {
		if (isSectorsRendered) dispatch(removeSiteIdFromRenderSector(siteFull?.id as string));
		else dispatch(addSiteIdToRenderSector(siteFull?.id as string));

		dispatch(setMarkerForShortInfo(null));
		dispatch(setShowMarkerShortInfo(false));
	};

	const isDataAvailable = isAssetsMode ? assetFull : siteFull;

	const isAssetChangeLocationAvailable = isAssetsMode && assetFull?.rentStatus?.name !== EAssetRentStatuses.active;

	const sectorBands = new Set(allSectors.map(sector => sector.band.name));
	const anyCellHasSectorSettings = cellsForSite?.cells?.some(cell => sectorBands.has(cell.band.name));
	const isSectorsAvailable = !isSectorsShownGlobally && anyCellHasSectorSettings;

	const showSectorButtonText = isSectorsRendered ? 'Hide sectors' : 'Show sectors';

	const isLoading = (
		!isCluster &&
		(
			(isSitesMode && (!siteFull?.id || isCellsLoading)) ||
			(isAssetsMode && !assetFull?.id)
		)
	);

	return (
		<div
			className={styles.markerInfoContainer}
			{...isCluster && {
				style: {
					minHeight: 'unset',
					minWidth: 'unset',
				},
			}}
		>
			{
				(isDataAvailable || markerShort) ? (
					<div
						className={styles.markerInfo}
						style={elementPosition}
					>
						{isCluster
							? <div className={styles.cluster}>
								<>
									Quantity: {markerShort.count}
								</>
							</div>
							: (
								<div>
									{isAssetsMode
										? assetFull &&
										<AssetInfo
											asset={assetFull}
											isAssetOwnerAvailable={isAssetOwnerAvailable}
											typeContainerMinWidth={typeContainerMinWidth}
										/>
										: siteFull && isSitesMode &&
										<SiteInfo
											site={siteFull}
											typeContainerMinWidth={typeContainerMinWidth}
										/>
									}
									{isAssetChangesAvailable && (
										<div className={styles.assetButtonsGroup}>
											<Button
												onClick={toggleDeleteConfirm}
												text='Delete asset'
												withoutBackground
											/>
											<Button
												disabled={!isAssetChangeLocationAvailable}
												onClick={enableAssetLocationEditMode}
												text='Change location'
												tooltip={!isAssetChangeLocationAvailable ? ASSET_LOCATION_EDIT_DISABLED_TOOLTIP : ''}
											/>
										</div>
									)}
									{(siteFull?.id
										&& (isSiteChangesAvailable || isKPIAnalysisButtonVisible || isSectorsAvailable)
									) && (
										<div className={cn(styles.buttonsGroup, styles.siteButtons)}>
											{isSectorsAvailable && (
												<Button
													fullWidth
													onClick={handleToggleSectors}
													secondary={isSectorsRendered}
													text={showSectorButtonText}
												/>
											)}
											{isSiteChangesAvailable && (
												<Button
													fullWidth
													onClick={enableSiteLocationEditMode}
													text='Change location'
												/>
											)}
											{isKPIAnalysisButtonVisible && (
												<Button
													disabled={!isKPIAnalysisButtonEnabled}
													fullWidth
													onClick={toggleKpiAnalysisModal}
													text='KPI Analysis'
													tooltip={!isKPIAnalysisButtonEnabled ? 'No data available for this site' : ''}
												/>
											)}
										</div>
									)}
								</div>
							)}
						<Modal
							isOpen={isDeleteConfirm}
							onClose={toggleDeleteConfirm}
						>
							<Confirm
								isLoading={isDeletingAsset}
								onClose={toggleDeleteConfirm}
								onSubmit={onDeleteAsset}
								question='Are you sure you want to delete the Asset?'
							/>
						</Modal>
						<Modal
							isOpen={isKpiAnalysisModalOpen}
							isScrollableContent
							onClose={toggleKpiAnalysisModal}
							title='Select KPI'
							width='400px'
						>
							<KpiSelectModalOnMap charts={kpiChartsForSite} />
						</Modal>
					</div>
				) : null
			}
			{isLoading && <SpinnerOverlay />}
		</div>
	);
};

export default MarkerShortInfo;
